<template>
    <div class="w-full">
        <div class="mb-4">
            <DesignSegmentedToggle
                v-model="isPending"
                :options="[
                    { text: 'Pending Approvals', value: true },
                    { text: 'Approved', value: false }
                ]"
                :fullWidth="false"
                :segmentWidth="180"
                :size="'LG'"
                @change="fetchApprovals"
            />
        </div>

        <div class="flex flex-col items-center gap-2 mb-4">
            <div class="w-full flex justify-center">
                <div class="w-full max-w-xl">
                    <ElementsDateSearchPackLite
                        v-model:startDate="selectDate.start" 
                        v-model:endDate="selectDate.end" 
                        @searchEvent="fetchApprovals" />
                </div>
            </div>
        </div>

        <div class="w-full max-w-2xl mb-4 flex gap-4">
            <DesignDropdownSelect
                v-model="walletCategorySeq"
                :options="walletCategoryOptions"
                size="LG"
                placeholder="All Wallets"
                :useDefaultMsg="true"
                @change="fetchApprovals"
            />

            <DesignDropdownSelect
                v-if="!isPending"
                v-model="selectedApprovalStatus"
                :options="[
                    { text: 'Approved', value: 'APPROVED' },
                    { text: 'Disapproved', value: 'REJECTED' },
                    { text: 'Cancelled', value: 'CANCELED' }
                ]"
                size="LG"
                placeholder="All Status"
                :useDefaultMsg="true"
                @change="fetchApprovals"
            />
        </div>

        <div class="mt-5 w-full">
            <table class="w-full table-fixed border border-gray-300 rounded-lg bg-white">
                <colgroup v-if="isPending">
                    <col style="width: 3rem">
                    <col style="width: 16rem">
                    <col style="width: 16rem">
                    <col style="width: 16rem">
                    <col style="width: 16rem">
                    <col style="width: 16rem">
                </colgroup>
                <colgroup v-else>
                    <col style="width: 3rem">
                    <col style="width: 8.5rem">
                    <col style="width: 8.5rem">
                    <col style="width: 8.5rem">
                    <col style="width: 8.5rem">
                    <col style="width: 8.5rem">
                    <col style="width: 12.75rem">
                    <col style="width: 8.5rem">
                </colgroup>
                <thead class="border-b border-gray-300 rounded-t-lg">
                    <tr>
                        <th scope="col" class="py-4 pl-4 pr-2 text-base text-slate-500 text-center">No</th>
                        <th scope="col" class="py-4 px-2.5 text-base text-slate-500 text-center">Request Date</th>
                        <th v-if="!isPending" scope="col" class="py-4 px-2 text-base text-slate-500 text-center">Approved Date</th>
                        <th scope="col" class="py-4 px-2 text-base text-slate-500 text-center">Wallet</th>
                        <th scope="col" class="py-4 px-2 text-base text-slate-500 text-center">Sent to</th>
                        <th scope="col" class="py-4 px-2 text-base text-slate-500 text-center">Point</th>
                        <th v-if="!isPending" scope="col" class="py-4 px-2 text-base text-slate-500 text-center">Status</th>
                        <th scope="col" class="py-4 px-2 text-base text-slate-500 text-center">Detail</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(row, index) in approvals"
                        :key="row.approvalFlexbenTransferSeq || index"
                        class="bg-white"
                        :class="{ 'bg-slate-50': index % 2 !== 0 }"
                    >
                        <td class="py-2.5 pl-4 pr-2 text-sm font-normal text-center">{{ index + 1 }}</td>
                        <td class="px-2 py-2.5 text-sm font-normal text-center">
                            <span
                                class="font-inter-heading-20 text-content-basic-1"
                                v-html="$dateFormatUtil.convertDateTimeToMMddyyyyHHmm(row.regDatetime)"
                            />
                        </td>
                        <td v-if="!isPending" class="px-2 py-2.5 text-sm font-normal text-center">
                            <span
                                class="font-inter-heading-20"
                                v-html="$dateFormatUtil.convertDateTimeToMMddyyyyHHmm(row.uptDatetime)"
                            />
                        </td>
                        <td class="px-2 py-2.5 text-sm font-normal text-center">
                            {{ row.walletCategoryName || '-' }}
                        </td>
                        <td class="px-2 py-2.5 text-sm font-normal text-center">
                            {{ getSentToLabel(row) }}
                        </td>
                        <td class="px-2 py-2.5 text-sm font-normal text-center">
                            {{ numberWithCommas(row.mileageVolume) }}P
                        </td>
                        <td v-if="!isPending" class="px-2 py-2.5 text-sm font-normal text-center">
                            <span :class="getStatusClass(row.approvalStatus)">
                                {{ approvalStatusMap[row.approvalStatus] }}
                            </span>
                        </td>
                        <td class="px-2 py-2.5 text-sm font-normal text-center">
                            <DesignButton
                                btnText="Detail"
                                btnSize="MD"
                                btnType="QUATERNARY"
                                :btnDisabled="false"
                                class="text-content-basic-1 border mx-auto"
                                @click-event="openDetail(row.approvalFlexbenTransferSeq)"
                            />
                        </td>
                    </tr>
                    <tr v-if="!approvals.length">
                        <td :colspan="columnCount" class="py-6 text-center text-sm text-content-basic-3">
                            No approvals found.
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    <Teleport to="body">
        <AppPopup
            v-if="showDetail"
            v-model="showDetail"
            :name="detail?.requestUnit==='DEPARTMENT' ? 'Recipient Department List' : 'Recipient Employee List'"
            :max-width="'600px'"
        >
            <div>
                <div class="pt-4 pb-5 px-5 flex flex-col gap-4">
                    <div class="flex gap-2 justify-start">
                        <div class="col-span-1 text-gray-600 font-inter-heading-20">Date</div>
                        <div class="font-inter-heading-20">{{ dateFormatter(detail.regDatetime, 'MM/DD/yyyy HH:mm:ss') }}</div>
                    </div>  
                    <div class="flex gap-2 justify-start">
                        <div class="col-span-1 text-gray-600 font-inter-heading-20">Wallet</div>
                        <div class="font-inter-heading-20">{{ detail.walletCategoryName }}</div>
                    </div>  
                    <div class="flex gap-2 justify-start">
                        <div class="col-span-1 text-gray-600 font-inter-heading-20">Sender</div>
                        <div class="font-inter-heading-20">{{ detail.requesterEmployeeName }}</div>
                    </div>
                    <div class="flex gap-2 justify-start" v-if="detail.approvalStatus !== 'REQUESTED'">
                        <div class="col-span-1 text-gray-600 font-inter-heading-20">Approver</div>
                        <div class="font-inter-heading-20">{{ detail.approverEmployeeName }}</div>
                    </div>
                    <div v-if="detail?.requestUnit==='DEPARTMENT'" >
                        <div class="border-y border-content-basic-4 flex justify-between px-3 py-3 bg-background-basic-2 text-content-basic-2 font-inter-heading-18">
                            <div>Department ({{ detail.totalDepartmentCount ?? (getDetailItems(detail).length) }})</div>
                            <div>Point</div>
                        </div>
                        <div v-for="(it, i) in getDetailItems(detail)" :key="i" class="border-b border-background-basic-3">
                            <div class="flex justify-between px-3 py-3 text-black font-inter-heading-19">
                                <div>{{ getItemName(it, 'DEPARTMENT') }}</div>
                                <div>{{ numberWithCommas(it.totalMileageVolume) }}</div>
                            </div>
                        </div>
                        <div class="flex justify-between px-3 py-3 bg-blue-50  font-inter-heading-18">
                            <div>Total Point</div>
                            <div class="text-primary-3">{{ numberWithCommas(detail.totalMileageVolume) }}</div>
                        </div>
                    </div>
                    
                    <div v-else-if="detail?.requestUnit==='PERSON'" >
                        <div class="border-y border-content-basic-4 flex justify-between px-3 py-3 bg-background-basic-2 text-content-basic-2 font-inter-heading-18">
                            <div>Employee ({{ detail.totalPersonCount ?? (getDetailItems(detail).length) }})</div>
                            <div>Point</div>
                        </div>
                        <div v-for="(it, i) in getDetailItems(detail)" :key="i" class="border-b border-background-basic-3">
                            <div class="flex justify-between px-3 py-3 text-black font-inter-heading-19">
                                <div>{{ getItemName(it, 'PERSON') }}</div>
                                <div>{{ numberWithCommas(it.mileageVolume) }}</div>
                            </div>
                        </div>
                        <div class="flex justify-between px-3 py-3 bg-blue-50  font-inter-heading-18">
                            <div>Total Point</div>
                            <div class="text-primary-3">{{ numberWithCommas(detail.totalMileageVolume) }}</div>
                        </div>
                    </div>
                    <div class="font-inter-heading-20">
                        <div class="text-gray-600 pb-[5px]">Message</div>
                        <div class="whitespace-pre-wrap break-all">{{ detail.transactionNote || '-' }}</div>
                    </div>
                </div>
                <hr/>
                <div class="px-5 py-4 flex justify-center">
                    <DesignButton btnText="Close" btnSize="MD" btnType="WHITE" :btnDisabled="false" 
                            class="!border !border-border-basic-2 !text-content-basic-2 px-10" 
                            @click-event="showDetail=false" 
                    />
                </div>
            </div>
        </AppPopup>
    </Teleport>
    </div>
</template>

<script>
import ElementsDateSearchPackLite from '@/components/elements/ElementsDateSearchPackLite.vue';
import DesignDropdownSelect from '@/components/design-system/design-dropdown-select.vue';
import DesignSegmentedToggle from '@/components/design-system/design-segmented-toggle.vue';
import DesignButton from '@/components/design-system/design-button.vue';
import AppPopup from '@/components/AppPopup.vue';
import moment from 'moment-timezone';

export default {
    name: 'TransferApprovals',
    components: { 
        ElementsDateSearchPackLite, 
        DesignDropdownSelect, 
        DesignSegmentedToggle, 
        DesignButton,
        AppPopup,
    },
    data() {
        return {
            isPending: true,
            selectedApprovalStatus: '',
            walletCategorySeq: '',
            walletCategoryOptions: [],
            selectDate: { start: null, end: null },
            approvals: [],
            // detail popup state
            showDetail: false,
            detail: null,
            approvalStatusMap: {
                'APPROVED': 'Approved',
                'REJECTED': 'Disapproved',
                'CANCELED': 'Cancelled',
            },
            requestUnitMap: {
                'PERSON': 'Employee',
                'DEPARTMENT': 'Department',
            },
        };
    },
    mounted() {
        this.initWeeklyRange();
        this.fetchWalletCategories();
        this.fetchApprovals();
    },
    computed: {
        columnCount() {
            return this.isPending ? 6 : 8;
        },
    },
    methods: {
         initWeeklyRange() {
            const base = moment().local();
            const start = base.clone().startOf('isoWeek').startOf('day');
            const end = base.clone().startOf('isoWeek').add(6, 'days').endOf('day');

            this.selectDate.start = start.toDate();
            this.selectDate.end   = end.toDate();

            console.log("START ::: ",this.selectDate.start);
        },
        toYYYYMMDD(date) {
            if (!(date instanceof Date)) return ''
            const y = date.getFullYear()
            const m = String(date.getMonth() + 1).padStart(2, '0')
            const d = String(date.getDate()).padStart(2, '0')
            return `${y}${m}${d}`
        },
        segBtnClass(key) {
            const active = this.selectedRange === key;
            return [
                'px-4 py-2 rounded-md border',
                active ? 'bg-primary-3 text-white border-primary-3' : 'bg-white text-content-basic-2 border-border-basic-2'
            ];
        },
        fetchWalletCategories() {
            const url = this.$api('uri', 'get-wallet-category-list');
            this.$axios.get(url).then(res => {
                this.walletCategoryOptions = (res.data?.data?.list || []).map(w => ({ text: w.walletCategoryName, value: w.walletCategorySeq }));
            });
        },
        fetchApprovals() {
            const url = this.$api('uri', 'get-flexben-approvals-by-requester');
            const start = this.toYYYYMMDD(this.selectDate.start)
            const end   = this.toYYYYMMDD(this.selectDate.end)

            const params = {
                isPending: this.isPending,
                approvalStatus: this.isPending ? '' : (this.selectedApprovalStatus || ''),
                walletCategorySeq: this.walletCategorySeq || '',
                startDate: start || '',
                endDate: end || '',
            };
            this.$axios.get(url, { params }).then(
                res => {
                this.approvals = res.data?.data?.list || [];
            }).catch(err => {
                console.error('fetchApprovals error', err);
                this.approvals = [];
            });
        },
        openDetail(approvalFlexbenTransferSeq) {
            const url = this.$api('uri', 'get-flexben-approval-detail').replace('{approvalFlexbenTransferSeq}', approvalFlexbenTransferSeq);
            this.$axios.get(url).then(res => {
                this.detail = res?.data?.data || null;
                this.showDetail = !!this.detail;
            }).catch(err => {
                console.error('get approval detail error', err);
                this.detail = null;
                this.showDetail = false;
            });
        },
        // requester view: no approve/disapprove
        numberWithCommas(v) {
            if (v == null || isNaN(v)) return '0';
            return String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        },
        getSentToLabel(item) {
            return this.requestUnitMap[item.requestUnit] || '-';
        },
        // detail helper: normalize details array across person/department response shapes
        getDetailItems(detail) {
            if (!detail) return [];
            // New API returns `details` array
            if (Array.isArray(detail.details)) return detail.details;
            // Backward compatibility
            if (Array.isArray(detail.employeeList)) return detail.employeeList;
            if (Array.isArray(detail.departmentList)) return detail.departmentList;
            return [];
        },
        getItemName(item, unit) {
            if (!item) return '-';
            return unit === 'DEPARTMENT' ? (item.departmentName || '-') : (item.employeeName || '-');
        },
        getStatusClass(status) {
            if (status === 'APPROVED') return 'text-primary-3';
            if (status === 'REJECTED') return 'text-accent-red-3';
            return 'text-content-basic-2';
        },
        dateFormatter(date, dateFormat = 'YYYY-MM-DD') {
            if (!date) {
                return;
            }
            return moment(date).format(dateFormat);
        },
    },
};
</script>

<style scoped></style>
